import { createSelector } from '@reduxjs/toolkit';
import { type FeatureSelectorWithFallback } from '@store/storeTypes';
import { type RootState } from '@store/types';

import { type ToolbarState } from './slice';

const emptyState: ToolbarState = {
  isVisible: false, // Match the slice default
  scrollControlEnabled: true,
  actions: {},
  actionContext: undefined,
  preferences: {
    position: 'bottom',
    compactMode: false,
  },
};

// Base selector
export const selectToolbarState: FeatureSelectorWithFallback<ToolbarState, typeof emptyState> = (
  state: RootState,
) => state.toolbar || emptyState;

// Visibility selectors
export const selectIsVisible = createSelector([selectToolbarState], state => state.isVisible);

export const selectScrollControlEnabled = createSelector(
  [selectToolbarState],
  state => state.scrollControlEnabled,
);

// Action context selectors
export const selectActionContext = createSelector(
  [selectToolbarState],
  state => state.actionContext,
);

// Actions selectors
export const selectActions = createSelector([selectToolbarState], state => state.actions);

export const selectActionById = (actionId: string) =>
  createSelector(
    [selectActions],
    actions =>
      actions[actionId] || {
        id: actionId,
        enabled: true,
        badgeCount: 0,
        metadata: undefined,
      },
  );

export const selectActionBadgeCount = (actionId: string) =>
  createSelector([selectActionById(actionId)], action => action.badgeCount);

export const selectActionEnabled = (actionId: string) =>
  createSelector([selectActionById(actionId)], action => action.enabled);

export const selectTotalBadgeCount = createSelector([selectActions], actions =>
  Object.values(actions).reduce((sum, action) => sum + action.badgeCount, 0),
);

// Preferences selectors
export const selectPreferences = createSelector([selectToolbarState], state => state.preferences);

export const selectPosition = createSelector(
  [selectPreferences],
  preferences => preferences.position,
);

export const selectCompactMode = createSelector(
  [selectPreferences],
  preferences => preferences.compactMode,
);

// Scroll container selector
export const selectScrollContainerSelector = createSelector(
  [selectToolbarState],
  state => state.scrollContainerSelector,
);

// Combined selectors
export const selectToolbarVisibility = createSelector(
  [selectIsVisible, selectScrollControlEnabled],
  (isVisible, scrollControlEnabled) => ({
    isVisible,
    scrollControlEnabled,
  }),
);
